import {
  CheckCircle,
  LinkSimple,
  PencilSimple,
  Stack,
  StackPlus,
  Star,
} from "@phosphor-icons/react"
import type { PresetView } from "~/components/planning/presetView"
import { VIEW_SHORTCUTS } from "~/constants/hotkeys"
import type { PageCommand } from "~/components/sidebar/search/types"
import { encodeViewParam, type ViewTarget } from "~/hooks/useViewRoute"
import type { SavedTableView } from "~/server/api/routers/tableView"
import type { ViewScope } from "~/server/api/utils/viewAccess"

/** A pill on the view bar: a saved view, a preset, or the Default pill of a page without presets */
export type ViewEntry =
  | { kind: "view"; view: SavedTableView }
  | { kind: "preset"; preset: PresetView }
  | { kind: "system" }

export type ViewCommandActions = {
  toggleFavorite: () => void
  toggleDefault: () => void
  copyLink: () => void
  edit: () => void
  saveAs: () => void
  select: (entry: ViewEntry) => void
}

export type ViewCommandsInput = {
  /** The pill showing now; null when nothing on the bar is selected */
  active: ViewEntry | null
  /** Preset pills; empty means the bar shows a Default pill instead */
  presets: readonly PresetView[]
  /** Saved-view pills; empty when the page withholds views */
  views: readonly SavedTableView[]
  supportsViews: boolean
  isDefault: (entry: ViewEntry) => boolean
  isFavorite: boolean
  activeScope?: ViewScope
  actions: ViewCommandActions
}

const DEFAULT_NAME = "Default"

const SCOPE_SECTION: Record<ViewScope, string> = {
  shared: "Shared",
  personal: "Personal",
  other: "Other users",
}

export const entryName = (entry: ViewEntry) => {
  if (entry.kind === "view") return entry.view.name
  if (entry.kind === "preset") return entry.preset.name
  return DEFAULT_NAME
}

export const entryTarget = (entry: ViewEntry): ViewTarget => {
  if (entry.kind === "view") return entry.view.id === undefined ? null : { viewId: entry.view.id }
  if (entry.kind === "preset") return { presetId: entry.preset.id }
  return null
}

export const isSameEntry = (a: ViewEntry | null, b: ViewEntry) => {
  if (!a || a.kind !== b.kind) return false
  if (a.kind === "view" && b.kind === "view") return a.view.id === b.view.id
  if (a.kind === "preset" && b.kind === "preset") return a.preset.id === b.preset.id
  return true
}

const editCommand = (input: ViewCommandsInput, section: string): PageCommand => {
  const base = {
    id: "view.edit",
    name: "Edit view…",
    keywords: ["rename", "update"],
    icon: <PencilSimple />,
    section,
  }
  const { active, activeScope, actions } = input
  if (active?.kind !== "view") {
    return { ...base, disabledReason: "Built-in views can't be edited", run: actions.edit }
  }
  if (activeScope === "shared") return { ...base, requires: "edit", run: actions.edit }
  if (activeScope === "other") {
    return { ...base, disabledReason: "You can only edit your own views", run: actions.edit }
  }
  return { ...base, run: actions.edit }
}

type SwitchEntry = { entry: ViewEntry; section: string; disabledReason?: string }

const presetDisabledReason = (preset: PresetView) => {
  if (!preset.disabled) return undefined
  return preset.tooltip ?? "Not available"
}

const builtInEntries = (presets: readonly PresetView[]): SwitchEntry[] => {
  if (presets.length === 0) return [{ entry: { kind: "system" }, section: "Built-in" }]
  return presets.map((preset) => ({
    entry: { kind: "preset", preset },
    section: "Built-in",
    disabledReason: presetDisabledReason(preset),
  }))
}

const switchCommand = (input: ViewCommandsInput, section: string): PageCommand => {
  const { active, presets, views, isDefault, actions } = input
  const entries: SwitchEntry[] = [
    ...builtInEntries(presets),
    ...views.map((view) => ({
      entry: { kind: "view", view } as const,
      section: view.type ? SCOPE_SECTION[view.type] : "Saved",
    })),
  ]
  return {
    id: "view.switch",
    shortcut: VIEW_SHORTCUTS.switchView.sequence,
    name: "Switch view…",
    keywords: ["open", "change", "views"],
    icon: <Stack />,
    section,
    placeholder: "Search views",
    subCommands: entries.map(({ entry, section: entrySection, disabledReason }) => ({
      id: `view.switch.${encodeViewParam(entryTarget(entry)) ?? "default"}`,
      name: entryName(entry),
      section: entrySection,
      disabledReason,
      checked: isSameEntry(active, entry),
      badge: isDefault(entry) ? "Default" : undefined,
      run: () => actions.select(entry),
    })),
  }
}

/** The view pill menu's actions for the active pill, plus a view switcher, as page commands */
export const buildViewCommands = (input: ViewCommandsInput): PageCommand[] => {
  const { active, activeScope, supportsViews, presets, isDefault, isFavorite, actions } = input
  if (!supportsViews && presets.length === 0) return []
  if (!active) return [switchCommand(input, "")]

  const section = entryName(active)
  const isSystem = active.kind === "system"
  const activeIsDefault = isDefault(active)
  const hasLink = !!entryTarget(active)
  const noLinkReason = hasLink ? undefined : `${section} has no link of its own`
  const personal = activeScope === "personal"
  const linkBlockedReason = noLinkReason ?? (personal ? "Only you can see this view" : undefined)

  return [
    {
      id: isFavorite ? "view.unfavorite" : "view.favorite",
      shortcut: VIEW_SHORTCUTS.favorite.sequence,
      name: isFavorite ? "Remove from favorites" : "Favorite view",
      keywords: ["star", "bookmark", "sidebar"],
      icon: <Star weight={isFavorite ? "fill" : "regular"} />,
      section,
      disabledReason: hasLink ? undefined : `${section} can't be added to favorites`,
      run: actions.toggleFavorite,
    },
    {
      // A system Default that is the default can't be cleared: there is nothing to fall back to
      id: activeIsDefault && !isSystem ? "view.clearDefault" : "view.setDefault",
      shortcut: VIEW_SHORTCUTS.toggleDefault.sequence,
      name: activeIsDefault && !isSystem ? "Clear default view" : "Set as default view",
      keywords: ["home", "landing", "start"],
      icon: <CheckCircle weight={activeIsDefault ? "fill" : "regular"} />,
      section,
      disabledReason: isSystem && activeIsDefault ? "Already your default view" : undefined,
      run: actions.toggleDefault,
    },
    {
      id: "view.copyLink",
      shortcut: VIEW_SHORTCUTS.copyLink.sequence,
      name: "Copy link to view",
      keywords: ["share", "url"],
      icon: <LinkSimple />,
      section,
      disabledReason: linkBlockedReason,
      run: actions.copyLink,
    },
    ...(supportsViews
      ? [
          editCommand(input, section),
          {
            id: "view.saveAs",
            name: "Save as new view…",
            keywords: ["create", "new"],
            icon: <StackPlus />,
            section,
            run: actions.saveAs,
          },
        ]
      : []),
    switchCommand(input, section),
  ]
}
